/**
 * Digitalização presencial dos documentos de um trabalhador.
 *
 * Muitos trabalhadores em Luanda não têm smartphone ou à-vontade para submeter
 * documentos sozinhos. Este ecrã existe para o supervisor os recolher no local:
 * escolhe o trabalhador, tira as fotos e submete.
 *
 * `capture="environment"` faz o telemóvel abrir directamente a câmara traseira
 * — no computador continua a ser um seletor de ficheiros normal.
 */
import React, { useState, useEffect } from 'react';
import { Camera, Check, X, Loader2, Search } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';

const CAMPOS = [
  { chave: 'profile_photo', rotulo: 'Foto do profissional', obrigatorio: true,  ajuda: 'Rosto visível, boa luz' },
  { chave: 'bi_photo',      rotulo: 'Bilhete de Identidade', obrigatorio: true, ajuda: 'Número legível' },
  { chave: 'address_proof', rotulo: 'Comprovativo de morada', obrigatorio: false, ajuda: 'Opcional' },
];

const ScanWorkerDocs = ({ onDone }) => {
  const { success, error } = useToast();
  const [trabalhadores, setTrabalhadores] = useState([]);
  const [procura, setProcura] = useState('');
  const [escolhido, setEscolhido] = useState(null);
  const [bi, setBi] = useState('');
  const [morada, setMorada] = useState('');
  const [ficheiros, setFicheiros] = useState({});
  const [previews, setPreviews] = useState({});
  const [aEnviar, setAEnviar] = useState(false);

  // Procura no servidor (endpoint mínimo, só trabalhadores), com um atraso
  // para não disparar um pedido por cada tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      api.get('/verification/workers-search', { params: { q: procura } })
        .then(({ data }) => setTrabalhadores(Array.isArray(data) ? data : []))
        .catch(() => setTrabalhadores([]));
    }, 300);
    return () => clearTimeout(t);
  }, [procura]);

  const escolherFicheiro = (chave, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { error('Imagem demasiado grande (máx. 5 MB)'); return; }
    setFicheiros(f => ({ ...f, [chave]: file }));
    setPreviews(p => ({ ...p, [chave]: URL.createObjectURL(file) }));
  };

  const submeter = async () => {
    if (!escolhido) { error('Escolha o profissional'); return; }
    if (!bi.trim()) { error('Indique o número do BI'); return; }
    if (!ficheiros.profile_photo || !ficheiros.bi_photo) {
      error('A foto do profissional e a do BI são obrigatórias'); return;
    }
    setAEnviar(true);
    try {
      const fd = new FormData();
      fd.append('bi_number', bi.trim());
      if (morada.trim()) fd.append('address', morada.trim());
      Object.entries(ficheiros).forEach(([k, v]) => fd.append(k, v));

      const { data } = await api.post(`/verification/worker/${escolhido.user_id || escolhido.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      success(data?.message || 'Documentos registados.');
      setEscolhido(null); setBi(''); setMorada(''); setFicheiros({}); setPreviews({});
      onDone?.();
    } catch (e) {
      error(e?.response?.data?.message || 'Erro ao registar os documentos');
    } finally {
      setAEnviar(false);
    }
  };

  const filtrados = trabalhadores; // a filtragem é feita no servidor

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <Camera className="w-5 h-5 text-[#00f1fe]" />
        <h3 className="text-white font-bold">Digitalizar documentos no local</h3>
      </div>
      <p className="text-gray-500 text-xs mb-4">
        Para profissionais que não conseguem submeter sozinhos pela app. No telemóvel, os botões abrem a câmara.
      </p>

      {/* Escolher o trabalhador */}
      {!escolhido ? (
        <div>
          <div className="relative mb-2">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={procura} onChange={(e) => setProcura(e.target.value)}
              placeholder="Procurar profissional por nome ou telefone"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm" />
          </div>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {filtrados.length === 0 ? (
              <p className="text-gray-500 text-sm py-3 text-center">Nenhum profissional encontrado.</p>
            ) : filtrados.map(t => (
              <button key={t.user_id || t.id} onClick={() => setEscolhido(t)}
                className="w-full text-left px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-white flex justify-between items-center">
                <span>{t.full_name}</span>
                <span className="text-gray-500 text-xs">{t.phone_number}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between bg-gray-800 rounded-lg px-3 py-2 mb-4">
            <div>
              <span className="text-white text-sm font-medium">{escolhido.full_name}</span>
              <span className="text-gray-500 text-xs block">{escolhido.phone_number}</span>
            </div>
            <button onClick={() => setEscolhido(null)} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid gap-2 mb-4">
            <input value={bi} onChange={(e) => setBi(e.target.value)} placeholder="Número do BI *"
              className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm" />
            <input value={morada} onChange={(e) => setMorada(e.target.value)} placeholder="Morada (opcional)"
              className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm" />
          </div>

          <div className="grid gap-3 mb-4">
            {CAMPOS.map(c => (
              <div key={c.chave} className="flex items-center gap-3">
                {previews[c.chave] ? (
                  <img src={previews[c.chave]} alt={c.rotulo}
                    className="w-14 h-14 rounded-lg object-cover border border-gray-700" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gray-800 border border-dashed border-gray-700 grid place-items-center">
                    <Camera className="w-5 h-5 text-gray-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">
                    {c.rotulo} {c.obrigatorio && <span className="text-red-400">*</span>}
                  </div>
                  <div className="text-xs text-gray-500">{c.ajuda}</div>
                </div>
                <label className="px-3 py-1.5 rounded-lg bg-[#2e5bff]/15 text-[#00f1fe] border border-[#2e5bff]/40 text-xs font-medium cursor-pointer whitespace-nowrap">
                  {previews[c.chave] ? 'Repetir' : 'Tirar foto'}
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => escolherFicheiro(c.chave, e.target.files?.[0])} />
                </label>
              </div>
            ))}
          </div>

          <button onClick={submeter} disabled={aEnviar}
            className="w-full py-2.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-500 disabled:opacity-60 flex items-center justify-center gap-2">
            {aEnviar ? <><Loader2 className="w-4 h-4 animate-spin" /> A enviar…</>
                     : <><Check className="w-4 h-4" /> Registar documentos</>}
          </button>
          <p className="text-gray-500 text-xs mt-2 text-center">
            Fica pendente de aprovação — registar não aprova.
          </p>
        </div>
      )}
    </div>
  );
};

export default ScanWorkerDocs;
